import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { AIAdvice } from "@/components/dashboard/coach/AIAdvice"
import { Button } from "@/components/ui/button"
import { ArrowLeft } from "lucide-react"
import { supabase } from "@/integrations/supabase/client"
import { ChatRoomsService } from "@/lib/services/chat/rooms-service"
import { logger } from "@/lib/services/logger-service"
import { useFeatureTracking } from "@/hooks/use-feature-tracking"

export function AIAnalysis() {
  const { caregiverId } = useParams()
  const navigate = useNavigate()
  const [roomId, setRoomId] = useState<string | undefined>()
  const [caregiverName, setCaregiverName] = useState<string>("")
  const [loading, setLoading] = useState(true)
  const { trackFeatureUsage } = useFeatureTracking()
  
  // Track AI analysis usage
  useEffect(() => {
    trackFeatureUsage('ai_analysis')
  }, [trackFeatureUsage])
  
  useEffect(() => {
    async function fetchRoomId() {
      setLoading(true)
      try {
        if (!caregiverId) return
        
        // Get specialist ID from current authenticated user
        const { data: userData } = await supabase.auth.getUser()
        const authUserId = userData.user?.id
        
        if (!authUserId) {
          logger.error('Failed to get auth user ID for AI analysis')
          return
        }
        
        // Get the specialist ID by querying the specialists table
        const { data: specialistData, error: specialistError } = await supabase
          .from('specialists')
          .select('id')
          .eq('auth_user_id', authUserId)
          .single()
        
        if (specialistError) {
          logger.error('Failed to get specialist ID', { specialistError })
          return
        }
        
        const specialistId = specialistData.id
        
        // Get caregiver details
        const { data: caregiverData, error: caregiverError } = await supabase
          .from('caregivers')
          .select('id, name')
          .eq('id', caregiverId)
          .single()
          
        if (caregiverError) {
          logger.error('Failed to fetch caregiver details', { caregiverError })
          return
        }
        
        setCaregiverName(caregiverData.name)
        
        // Find or create a chat room
        try {
          // Direct query to get the room ID
          const { data: roomData, error: roomError } = await supabase
            .from('chat_rooms')
            .select('id')
            .eq('specialist_id', specialistId)
            .eq('caregiver_id', caregiverId)
            .single()
          
          if (roomError) {
            // If no room exists yet, create one
            if (roomError.code === 'PGRST116') {
              const room = await ChatRoomsService.findOrCreateChatRoom(specialistId, caregiverId)
              setRoomId(room.id)
              logger.info('Created new room for AI analysis', { roomId: room.id })
            } else {
              logger.error('Failed to query chat room', { roomError })
            }
          } else if (roomData) {
            setRoomId(roomData.id)
            logger.info('Retrieved existing room for AI analysis', { roomId: roomData.id })
          }
        } catch (error) {
          logger.error('Failed to create/find chat room', { error })
        }
      } catch (error) {
        logger.error('Error in AI analysis page setup', { error })
      } finally {
        setLoading(false)
      }
    }
    
    fetchRoomId()
  }, [caregiverId])
  
  return (
    <div className="container mx-auto py-8 px-4 max-w-4xl">
      <div className="mb-8 flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/coach-dashboard')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">AI Analysis</h1>
          {caregiverName && (
            <p className="text-muted-foreground">
              for {caregiverName}
            </p>
          )}
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow p-6">
        {roomId && caregiverId ? (
          <div className="mb-8">
            <AIAdvice caregiverId={caregiverId} />
          </div>
        ) : (
          <div className="text-center p-10 border rounded-lg bg-white">
            <p>Loading AI Analysis...</p>
          </div>
        )}
      </div>
    </div>
  )
}
